import React, { useState } from 'react';
import styles from '../../pages/dashboard/DashboardUI.module.css';
import settingsStyles from '../../pages/dashboard/Settings.module.css';
import { createCustomField, updateCustomField } from '../../services/customFieldService';
import { CUSTOM_FIELD_TYPES, OPTION_FIELD_TYPES } from '../../utils/customFieldUtils';

export const CUSTOM_FIELD_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#84cc16',
  '#6366f1', '#14b8a6', '#ef4444', '#6b7280'
];

export const CUSTOM_FIELD_ENTITIES = [
  { value: 'lead', label: 'Leads' },
  { value: 'project', label: 'Projects' },
];

const blankForm = (defaultEntity) => ({
  name: '',
  field_type: 'text',
  description: '',
  applies_to: [defaultEntity],
  options: [],
});

const formFromField = (field) => ({
  name: field.name,
  field_type: field.field_type,
  description: field.description || '',
  applies_to: [...(field.applies_to || [])],
  options: (field.options || []).map((o) => ({ ...o })),
});

/**
 * Create/edit dialog for a custom field.
 *
 * Shared between the Settings tab and the "+" column button on the Leads and
 * Projects tables, so a field created from either route is defined identically.
 * Owns its own submit, and reports back through onSaved so each caller can
 * refresh however it needs to.
 */
export default function CustomFieldModal({
  field = null,          // editing when set, creating when null
  defaultEntity = 'lead',
  onClose,
  onSaved,
  onMessage,
}) {
  const [formData, setFormData] = useState(() => (
    field ? formFromField(field) : blankForm(defaultEntity)
  ));
  const [saving, setSaving] = useState(false);

  const takesOptions = OPTION_FIELD_TYPES.includes(formData.field_type);

  const toggleEntity = (entity) => {
    setFormData((prev) => ({
      ...prev,
      applies_to: prev.applies_to.includes(entity)
        ? prev.applies_to.filter((e) => e !== entity)
        : [...prev.applies_to, entity],
    }));
  };

  const addOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [
        ...prev.options,
        {
          value: `option_${prev.options.length + 1}`,
          label: '',
          color: CUSTOM_FIELD_COLORS[prev.options.length % CUSTOM_FIELD_COLORS.length],
        },
      ],
    }));
  };

  const updateOption = (index, patch) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => (i === index ? { ...o, ...patch } : o)),
    }));
  };

  const removeOption = (index) => {
    setFormData((prev) => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!formData.name.trim()) {
      onMessage({ message: 'Field name is required', type: 'error' });
      return;
    }
    if (formData.applies_to.length === 0) {
      onMessage({ message: 'Choose at least one of Leads or Projects', type: 'error' });
      return;
    }
    if (takesOptions && formData.options.filter((o) => o.label.trim()).length === 0) {
      onMessage({ message: 'Select fields need at least one option', type: 'error' });
      return;
    }

    const payload = {
      name: formData.name.trim(),
      field_type: formData.field_type,
      description: formData.description.trim(),
      applies_to: formData.applies_to,
      options: takesOptions
        ? formData.options
            .filter((o) => o.label.trim())
            .map((o) => ({ value: o.value, label: o.label.trim(), color: o.color }))
        : undefined,
    };

    try {
      setSaving(true);
      const result = field
        ? await updateCustomField(field.field_id, payload)
        : await createCustomField(payload);
      onMessage({
        message: field ? 'Field updated successfully' : `"${payload.name}" added`,
        type: 'success',
      });
      await onSaved(result.field);
      onClose();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to save field';
      onMessage({ message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={settingsStyles.modalOverlay} onClick={onClose}>
      <div className={settingsStyles.modal} onClick={(e) => e.stopPropagation()} style={{ minHeight: 'auto' }}>
        <div className={settingsStyles.modalHeader}>
          <h3>{field ? 'Edit Field' : 'Create Field'}</h3>
          <button className={settingsStyles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={settingsStyles.modalContent}>
          <form onSubmit={handleSubmit}>
            <div className={settingsStyles.formGroup}>
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Site Reference, Tender Number"
                autoFocus
              />
            </div>

            <div className={settingsStyles.formGroup}>
              <label>Type</label>
              <select
                value={formData.field_type}
                disabled={Boolean(field)}
                onChange={(e) => setFormData({ ...formData, field_type: e.target.value, options: [] })}
              >
                {CUSTOM_FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {field && (
                <p className={settingsStyles.cardDesc} style={{ margin: '6px 0 0 0' }}>
                  A field's type can't be changed once it holds data. Delete it and add a new one instead.
                </p>
              )}
            </div>

            <div className={settingsStyles.formGroup}>
              <label>Show on</label>
              <div style={{ display: 'flex', gap: '16px', paddingTop: '4px' }}>
                {CUSTOM_FIELD_ENTITIES.map(({ value, label }) => (
                  <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={formData.applies_to.includes(value)}
                      onChange={() => toggleEntity(value)}
                    />
                    {label}
                  </label>
                ))}
              </div>
              <p className={settingsStyles.cardDesc} style={{ margin: '6px 0 0 0' }}>
                Applied to both, the value copies over when a lead is accepted into a project.
              </p>
            </div>

            {takesOptions && (
              <div className={settingsStyles.formGroup}>
                <label>Options</label>
                {formData.options.map((option, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => updateOption(index, { label: e.target.value })}
                      placeholder={`Option ${index + 1}`}
                      style={{ flex: 1 }}
                    />
                    <div className={settingsStyles.colorPicker} style={{ flexWrap: 'nowrap' }}>
                      {CUSTOM_FIELD_COLORS.slice(0, 6).map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`${settingsStyles.colorSwatch} ${option.color === color ? settingsStyles.colorSwatchActive : ''}`}
                          style={{ backgroundColor: color }}
                          onClick={() => updateOption(index, { color })}
                          aria-label={`Colour option ${index + 1}`}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.buttonSecondary}
                      onClick={() => removeOption(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" className={styles.buttonSecondary} onClick={addOption}>
                  + Add Option
                </button>
              </div>
            )}

            <div className={settingsStyles.formGroup}>
              <label>Description (optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What is this field for?"
                rows={2}
              />
            </div>

            <div className={settingsStyles.modalActions}>
              <button className={styles.buttonSecondary} onClick={onClose} type="button">
                Cancel
              </button>
              <button className={styles.buttonPrimary} type="submit" disabled={saving}>
                {saving ? 'Saving...' : field ? 'Save Changes' : 'Create Field'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
